import { Box, TextField, Typography } from '@mui/material'
import { FC, useState } from 'react'
import type { ChangeEvent, FocusEvent } from 'react'
import { ServerVisitReason, toWireReason, VisitReason } from '../AppointmentService'
import { SymptomItem } from './PrimaryConcern'

type BookProps = {
  apptReason: string
  visitReason: VisitReason | null
  isTriageFlow: boolean
  setApptReason: (reason: string) => void
  saving: boolean
  symptomsSelected: SymptomItem[]
  otherSymptomsSelected: string[]
}

// A triage booking already carries the member's concerns, so its description is
// optional. Every other visit type requires one; the placeholder wording varies
// by type, keyed by wire reason so both wizards share it.
const TRIAGE_PLACEHOLDER = 'Additional information (Member-Facing)'
const DEFAULT_PLACEHOLDER = 'Reason for visit (Member-Facing)'
const REASON_PLACEHOLDER: Partial<Record<ServerVisitReason, string>> = {
  'Video-New': 'Concerns to discuss (Member-Facing)',
  'Annual Wellness Visit Established': 'Concerns to discuss (Member-Facing)',
  'Annual Wellness Visit New': 'Concerns to discuss (Member-Facing)',
  'Behavioral Health': 'Reason for visit (Member-Facing)',
  'Health Guide Consult': 'Reason for visit (Member-Facing)',
}

const BookingReason: FC<BookProps> = ({
  apptReason,
  setApptReason,
  visitReason,
  isTriageFlow,
  saving,
  symptomsSelected,
  otherSymptomsSelected,
}) => {
  const [reasonError, setReasonError] = useState<boolean>(false)
  let symptomsList: string[] = symptomsSelected.map(({ label }) => label)
  if (otherSymptomsSelected.length) {
    symptomsList = symptomsList.concat(otherSymptomsSelected)
  }

  const placeholder = isTriageFlow
    ? TRIAGE_PLACEHOLDER
    : (visitReason != null && REASON_PLACEHOLDER[toWireReason(visitReason)]) || DEFAULT_PLACEHOLDER

  const updateApptReason = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value == '' && visitReason && !isTriageFlow) {
      setReasonError(true)
    }
  }

  const handleUpdateApptReason = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setApptReason(event.target.value)
    if (event.target.value !== '' || isTriageFlow) {
      setReasonError(false)
    } else {
      setReasonError(true)
    }
  }

  return (
    <Box sx={{ m: 2 }}>
      {isTriageFlow ? (
        <Box sx={{ my: 2 }}>
          <Typography>
            {'Primary concerns: '}
            {symptomsList.join(', ')}
          </Typography>
        </Box>
      ) : null}
      <TextField
        id="outlined-basic"
        variant="outlined"
        value={apptReason}
        onChange={handleUpdateApptReason}
        multiline={true}
        fullWidth
        placeholder={placeholder}
        disabled={saving}
        error={reasonError}
        helperText={reasonError && 'Reason for visit required'}
        required={!isTriageFlow}
        onBlur={updateApptReason}
      />
    </Box>
  )
}

export default BookingReason
